import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { debounceTime } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators';

import { Constants } from 'src/shared/utilities/constants.util';
import { HoursCalculator } from 'src/shared/models/Hours-calculator.model';
import { JobTitle } from 'src/shared/models/job-title.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { StaffRatioValue } from 'src/shared/models/staff-ratio-value.model';
import { TotalUosVariance } from '../../base-classes/total-uos-variance.component';
import { WorkflowConstants } from 'src/app/workflow/workflow-constants.util';

@Component({
    selector: 'smt-enter-actual-uos-block',
    templateUrl: './enter-actual-uos-block.component.html',
    styleUrls: ['./enter-actual-uos-block.component.scss']
})
export class EnterActualUosComponent extends TotalUosVariance implements OnInit {
    @Input() shiftRecord: ShiftRecord;
    @Input() enterActualForm: UntypedFormGroup;
    @Input() enterByBlock: boolean;
    private showFixedStaff = false;
    private showHoursCalculatorModal = false;
    private staffWarningRows: HoursCalculator[] = [];
    private targetStaffs: StaffRatioValue[] = [];

    private enableFixedJobEditing = false;
    loadingRecords: boolean;
    
    constructor() { 
        super();
    }

    public ngOnInit() {    
        this.initialize();
    }

    private closeCalculatorModal() {     
        this.showHoursCalculatorModal = false
    }        

    private getActualControlName(jobTitleId: number): string {
        return 'actual-' + this.getJobAndShiftBlockControlName(jobTitleId);
    } 

    private getFixedJobTitles(): JobTitle[] {
        if (!this.shiftRecord || !this.shiftRecord.costCenter || !MiscUtil.checkArray(this.shiftRecord.costCenter.fixedJobTitles)) {
            return [];
        }

        return this.shiftRecord.costCenter.fixedJobTitles;
    }

    private getJobAndShiftBlockControlName(jobTitleId: number) : string {
        return 'job-' + jobTitleId + '-' + this.getShiftBlockControlName();
    }

    private getJobTitles(): JobTitle[] {
        if (!this.shiftRecord || !this.shiftRecord.costCenter || !MiscUtil.checkArray(this.shiftRecord.costCenter.jobTitles)) {
            return [];
        }

        return this.shiftRecord.costCenter.jobTitles;
    }

    private getPlannedControlName(jobTitleId: number): string {
        return 'planned-' + this.getJobAndShiftBlockControlName(jobTitleId);
    }

    private getPlannedValue(jobTitleId: number): number {
        if (this.shiftRecord && this.shiftRecord.staffingPlans) {
            let staffingPlan = this.shiftRecord.staffingPlans.find(s => s.jobTitleId == jobTitleId);
            
            if (staffingPlan) {                
                return staffingPlan.projected;
            }
        }

        return null;
    }

    private getRecommendedValue(jobTitleId: number) : string {
        let staffValue = 0;

        if (this.shiftRecord && this.shiftRecord.staffingPlans) {
            let plans = this.shiftRecord.staffingPlans.filter(x => x.jobTitleId == jobTitleId);

            if (MiscUtil.checkArray(plans)) {
                plans.forEach(s => { staffValue += s.recommended; });

                let isVariable = this.shiftRecord.costCenter.variableJobTitles.length > 0 &&
                    this.shiftRecord.costCenter.variableJobTitles.findIndex(i => i.id == jobTitleId) > -1;

                if (isVariable){
                    staffValue = Math.ceil(staffValue); 
                }

                return staffValue.toString();
            }
        }

        return undefined;
    }

    private getShiftBlockControlName(): string {
        return 'block-' + this.shiftRecord.shiftBlockId;
    }

    private getStaffWarning(jobTitleId: number): string {
        let row =  this.staffWarningRows.find(a => a.JobTitle.id == jobTitleId);
        let warning: string[] = [];      

        if (MiscUtil.isValidPositiveNumber(row.FourHoursStaff)) {
            warning.push(` 4 hours - ${row.FourHoursStaff} Staff`) ;
        }

        if (MiscUtil.isValidPositiveNumber(row.EightHoursStaff)) {
            warning.push(` 8 hours - ${row.EightHoursStaff} Staff`) ;
        }

        if(MiscUtil.isValidPositiveNumber(row.TenHoursStaff)) {
            warning.push(` 10 hours - ${row.TenHoursStaff} Staff`);
        }

        if(MiscUtil.isValidPositiveNumber(row.TwelveHoursStaff)) {
            warning.push(` 12 hours - ${row.TwelveHoursStaff} Staff`);
        }
        
        return `${row.TotalHours} Total Hours: ` + warning.toString();
    }

    private getTargetControlName(jobTitleId: number): string {
        return 'target-' + this.getJobAndShiftBlockControlName(jobTitleId);
    }

    private getVariableJobTitles(): JobTitle[] {
        if (!this.shiftRecord || !this.shiftRecord.costCenter || !MiscUtil.checkArray(this.shiftRecord.costCenter.variableJobTitles)) {
            return [];
        }

        return this.shiftRecord.costCenter.variableJobTitles;
    }

    private hasFixedJobs(): boolean {
        return MiscUtil.checkArray(this.getFixedJobTitles());
    }

    private hasJobs(): boolean {
        return MiscUtil.checkArray(this.getJobTitles());
    }

    private hasVariableJobsByShiftBlockId(): boolean {
        return MiscUtil.checkArray(this.getVariableJobTitles());
    }       
    
    private initialize() {          
        this.getJobTitles().forEach(jobTitle => {                                                                                
            this.setPlannedControl(jobTitle.id);
            this.setTargetControl(jobTitle.id);  
            this.setActualControl(jobTitle.id, jobTitle.abbreviation);            
        }); 

        // Angular does not like adding controls to this form where some are enabled and some are disabled.
        // Thus we add all the controls as disabled by default.  This will evaluate the actual controls
        // and determine if they need to be enabled.  The setTimeout gets past a known issue in angular
        // lifecycles to prevent an ExpressionChangedAfterItWasCheckedError.
        setTimeout(() => { 
            this.shiftRecord.costCenter.jobTitles.forEach(jobTitle => {
                let actualControl = this.enterActualForm.controls[this.getActualControlName(jobTitle.id)];
                let plannedControl = this.enterActualForm.controls[this.getPlannedControlName(jobTitle.id)];
                let targetControl = this.enterActualForm.controls[this.getTargetControlName(jobTitle.id)];
                let isFixed = this.getFixedJobTitles().some(j => j.id == jobTitle.id);
                
                if (MiscUtil.isValidNonNegativeNumber(actualControl.value) && 
                    MiscUtil.isValidNonNegativeNumber(targetControl.value)) {
                    this.updateJobTitlesWithVariance(
                        isFixed, 
                        jobTitle.abbreviation, 
                        jobTitle.id, 
                        +actualControl.value, 
                        +targetControl.value);
                }                

                if ((!isFixed || (isFixed && this.enableFixedJobEditing)) && 
                    targetControl && MiscUtil.isValidNonNegativeNumber(targetControl.value) && 
                    plannedControl && MiscUtil.isValidNonNegativeNumber(plannedControl.value)) {
                    actualControl.enable();
                }
            });
        });
    }

    private async initializeTargetStaff() {
        this.targetStaffs=[];
        this.shiftRecord.costCenter.variableJobTitles.forEach(jobTitle => {
            let targetstaff: StaffRatioValue = new StaffRatioValue();
            let staffValue = this.getRecommendedValue(jobTitle.id);

            targetstaff.jobTitleId=jobTitle.id;
            
            if (MiscUtil.isValidNumber(+staffValue)) {
                targetstaff.staffValue = +staffValue;
            }            

            this.targetStaffs.push(targetstaff);
        })
    }

    private openHoursCalculator() {  
        this.initializeTargetStaff();
        this.showHoursCalculatorModal = true;
    }

    private setActualControl(jobTitleId: number, jobTitleAbbreviation: string) {
        // The target control value is needed for calculating variance, the planned control value is needed for saving the actual.
        // If either of these are missing then and actual value cannot be entered and saved.
        let targetControl = this.enterActualForm.controls[this.getTargetControlName(jobTitleId)];
        
        // Angular does not agree with dynamically adding enabled and disabled controls to this component.  It is fine with
        // all disabled or all enabled.  To get around this we are going to create the actuals controls and disable them
        // by default.  Later they will be evauluated to determine if the should be enabled or not.  For some reason angular
        // is ok with this.
        let actualControl = new UntypedFormControl({ value: '', disabled: true });

        if (MiscUtil.isValidNonNegativeNumber(this.getPlannedValue(jobTitleId)) &&
            targetControl &&
            MiscUtil.isValidNonNegativeNumber(targetControl.value) &&
            this.shiftRecord && 
            MiscUtil.checkArray(this.shiftRecord.staffingPlans)) {
            let staffingPlan = this.shiftRecord.staffingPlans.find(s => s.jobTitleId == jobTitleId);

            if (staffingPlan) {
                let amount: number = null;
                if (MiscUtil.isValidNonNegativeNumber(staffingPlan.amount)) {
                    actualControl.setValue(staffingPlan.amount);
                    amount = staffingPlan.amount; 
                }
                else if (this.shiftRecord.costCenter.fixedJobTitles.some(j => j.id == jobTitleId) && MiscUtil.isValidNonNegativeNumber(targetControl.value)){
                    actualControl.setValue(targetControl.value);
                    amount = +targetControl.value; 
                }
                else {
                    actualControl.setValue('');
                }

                this.updateStaffingPlan(jobTitleId, amount);

                actualControl.setValidators([ 
                    Validators.required, 
                    Validators.min(WorkflowConstants.UosMinValue), 
                    Validators.max(WorkflowConstants.UosMaxValue), 
                    Validators.pattern(Constants.NoDecimalRegex)
                ]);

                actualControl.valueChanges
                    // There is a bug in angular that causes valueChanges subscriptions to fire twice for number inputs.
                    // distinctUntilChanged is an rxjs operator that can be used as a work around until SMT is upgraded
                    // to a version of angular where this bug is corrected.
                    .pipe(distinctUntilChanged())
                    .pipe(debounceTime(Constants.DebounceTime))
                    .subscribe(value => {                        
                        let isFixed = this.getFixedJobTitles().some(j => j.id == jobTitleId);
                        
                        this.updateJobTitlesWithVariance(isFixed, jobTitleAbbreviation, jobTitleId, value, targetControl.value);   

                        if (MiscUtil.isValidNonNegativeNumber(value)) {                            
                            this.updateStaffingPlan(jobTitleId, value);                              
                        }
                        else {
                            actualControl.setValue('');
                            this.updateStaffingPlan(jobTitleId, null);   
                        }
                    });                  
            }            
        }
        
        this.enterActualForm.addControl(this.getActualControlName(jobTitleId), actualControl);
    }

    private setPlannedControl(jobTitleId: number) {
        let plannedControl = new UntypedFormControl();
        let plannedValue = this.getPlannedValue(jobTitleId);

        if (MiscUtil.isValidNonNegativeNumber(plannedValue)) {
            plannedControl.setValue(plannedValue);
        }
        else {
            plannedControl.setValue('*');
        }
        
        plannedControl.disable();

        this.enterActualForm.addControl(this.getPlannedControlName(jobTitleId), plannedControl);
    }

    private setTargetControl(jobTitleId: number) {
        let targetControl = new UntypedFormControl();     
        let targetValue = this.getRecommendedValue(jobTitleId);

        if (targetValue) {
            targetControl.setValue(targetValue);
        }
        else {
            targetControl.setValue('*');
        }
          
        targetControl.disable();      

        this.enterActualForm.addControl(this.getTargetControlName(jobTitleId), targetControl);
    }

    private showPlannedWarning(): boolean {
        let jobTitles = this.getJobTitles();

        for(let jobTitle of jobTitles) {
            let plannedValue = this.getPlannedValue(jobTitle.id);

            if (!MiscUtil.isValidNonNegativeNumber(plannedValue)) {
                return true;
            }
        }
        
        return false;
    }

    private showHoursWarning(rows: any) {
        this.showHoursCalculatorModal = false;
        this.staffWarningRows = rows;
    }

    private showStaffWarning(): boolean {
        return MiscUtil.checkArray(this.staffWarningRows);
    }

    private toggleFixedJobEditing() {
        this.enableFixedJobEditing = !this.enableFixedJobEditing;

        // If the fixed staff is not visible then show it.
        if (!this.showFixedStaff) {
            this.showFixedStaff = true;
        }

        this.shiftRecord.costCenter.fixedJobTitles.map(j => j.id).forEach(jobTitleId => {
            let targetControl = this.enterActualForm.controls[this.getTargetControlName(jobTitleId)];
            let plannedControl = this.enterActualForm.controls[this.getPlannedControlName(jobTitleId)];
            let actualControl = this.enterActualForm.controls[this.getActualControlName(jobTitleId)];

            if (targetControl && MiscUtil.isValidNonNegativeNumber(targetControl.value) &&
                plannedControl && MiscUtil.isValidNonNegativeNumber(plannedControl.value) &&
                actualControl) {
                if (this.enableFixedJobEditing) {
                    actualControl.enable();
                }
                else {
                    actualControl.disable();
                }
            }
        });
    }

    private toggleFixedStaff() {
        this.showFixedStaff = !this.showFixedStaff;
    }

    private updateStaffingPlan(jobTitleId: number, amount: number) {
        if (this.shiftRecord && MiscUtil.checkArray(this.shiftRecord.staffingPlans)) {
            let staffingPlan = this.shiftRecord.staffingPlans.find(s => s.jobTitleId == jobTitleId);

            if (staffingPlan) {
                staffingPlan.amount = amount;
            }
        }
    }
}
